import { FC, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeExternalLinks from 'rehype-external-links';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { twJoin } from 'tailwind-merge';

import { FormError } from '@dbt-labs/sourdough';

import { DescriptionForm } from './DescriptionForm';

interface InheritedDescriptionDisplayProps {
  /** The description from the resource */
  description: string | null | undefined;

  /** The description to use in the event the description or inheritedDescription are not provided */
  defaultDescription: string | undefined;

  /** Controls whether to display the "[Inherited from ...]" subtext */
  isDescriptionInherited: boolean;

  inheritedInfo: React.ReactNode;
  className?: string | undefined;
}

/**
 * This component is used to display the description of a resource,
 * and if it is inherited from another location.
 */
export const InheritedDescriptionDisplay: FC<InheritedDescriptionDisplayProps> = ({
  description,
  defaultDescription,
  isDescriptionInherited,
  inheritedInfo,
  className,
}) => {
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionError, setDescriptionError] = useState('');
  const currDescription = description || defaultDescription;

  return (
    <>
      <DescriptionForm
        currDescription={currDescription}
        isDescriptionInherited={isDescriptionInherited}
        editingDescription={editingDescription}
        setEditingDescription={setEditingDescription}
        descriptionInfo={
          !editingDescription ? (
            <ReactMarkdown
              className={twJoin(
                'prose prose-sm mt-2 text-sm text-fgMain dark:prose-invert',
                className,
                (isDescriptionInherited || !description) && 'italic',
              )}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, [rehypeExternalLinks, { target: '_blank' }]]}
            >
              {currDescription}
            </ReactMarkdown>
          ) : null
        }
        onError={setDescriptionError}
      />
      {isDescriptionInherited && (
        <div className="mt-1 text-sm italic text-fgDecorative">
          [Inherited from {inheritedInfo}]
        </div>
      )}
      {!!descriptionError && <FormError error={descriptionError} />}
    </>
  );
};
